import { useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { useStore } from '../../context/StoreContext';
import { formatBRL } from '../../utils/format';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight, ShieldCheck } from 'lucide-react';

export function CartDrawer({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { isCartOpen, closeCart, items, subtotalCents, updateQuantity, removeItem } = useCart();
  const { settings } = useStore();

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    if (isCartOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isCartOpen, closeCart]);

  if (!isCartOpen) return null;

  const freeThreshold = settings.shipping_free_threshold_cents || 45000;
  const remainingForFree = Math.max(0, freeThreshold - subtotalCents);
  const progressPercent = Math.min(100, Math.round((subtotalCents / freeThreshold) * 100));

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true" aria-label="Sua Sacola de Compras">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-xs transition-opacity duration-300"
        onClick={closeCart}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-surface text-ink shadow-2xl flex flex-col border-l border-border-subtle animate-fadeIn">
          {/* Header */}
          <div className="p-5 border-b border-border-subtle flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-serif font-medium tracking-tight">Sua Sacola</h2>
              <span className="text-xs text-ink-muted bg-surface-muted px-2 py-0.5 rounded-full">
                {items.length} {items.length === 1 ? 'item' : 'itens'}
              </span>
            </div>
            <button
              onClick={closeCart}
              className="p-1.5 text-ink-muted hover:text-ink transition-colors rounded-sm"
              aria-label="Fechar sacola"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Free Shipping Progress Indicator */}
          <div className="bg-background-alt px-5 py-3 border-b border-border-subtle text-xs">
            {remainingForFree > 0 ? (
              <p className="text-ink-muted mb-1.5">
                Faltam <span className="font-medium text-ink">{formatBRL(remainingForFree)}</span> para você ganhar <span className="font-medium text-primary">Frete Cortesia</span>
              </p>
            ) : (
              <p className="font-medium text-olive flex items-center gap-1.5 mb-1.5">
                <ShieldCheck className="w-4 h-4" /> Parabéns! Você ganhou Frete Cortesia.
              </p>
            )}
            <div className="w-full bg-border-subtle h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-primary h-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Items List */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-3">
                <div className="w-16 h-16 rounded-full bg-surface-muted flex items-center justify-center text-ink-muted">
                  <ShoppingBag className="w-7 h-7 stroke-1" />
                </div>
                <h3 className="text-base font-serif font-medium">Sua sacola está vazia</h3>
                <p className="text-xs text-ink-muted max-w-xs leading-relaxed">
                  Descubra cerâmicas autorais, peças têxteis em linho puro e criações exclusivas do nosso catálogo.
                </p>
                <button
                  onClick={() => {
                    closeCart();
                    onNavigate('/catalogo');
                  }}
                  className="mt-2 text-xs uppercase tracking-wider font-medium text-primary hover:text-primary-hover underline underline-offset-4"
                >
                  Explorar Catálogo &rarr;
                </button>
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.cart_item_id}
                  className="flex gap-4 p-3 bg-surface border border-border-subtle rounded-sm transition-all hover:border-ink/20"
                >
                  <img
                    src={item.cover_image || 'https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?auto=format&fit=crop&w=200&q=80'}
                    alt={item.product_title}
                    className="w-18 h-20 object-cover rounded-xs bg-surface-muted shrink-0"
                  />
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-serif font-medium text-ink leading-snug line-clamp-1">
                        {item.product_title}
                      </h4>
                      <p className="text-xs text-ink-muted mt-0.5">{item.variant_title}</p>
                      <p className="text-xs font-medium text-primary mt-1">
                        {formatBRL(item.price_cents)}
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <div className="flex items-center border border-border-subtle rounded-xs bg-surface">
                        <button
                          onClick={() => updateQuantity(item.cart_item_id, item.quantity - 1)}
                          className="p-1 hover:text-primary transition-colors disabled:opacity-30"
                          aria-label="Diminuir quantidade"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-xs px-2 min-w-[20px] text-center font-mono">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.cart_item_id, item.quantity + 1)}
                          className="p-1 hover:text-primary transition-colors"
                          aria-label="Aumentar quantidade"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      <button
                        onClick={() => removeItem(item.cart_item_id)}
                        className="text-ink-muted hover:text-red-600 transition-colors p-1"
                        aria-label="Remover item da sacola"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer & Checkout Action */}
          {items.length > 0 && (
            <div className="p-5 border-t border-border-subtle bg-surface space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink-muted">Subtotal</span>
                <span className="font-mono font-medium text-base text-ink">
                  {formatBRL(subtotalCents)}
                </span>
              </div>
              <p className="text-[11px] text-ink-muted leading-tight">
                Frete e prazos calculados na etapa seguinte com base no CEP.
              </p>

              <button
                onClick={() => {
                  closeCart();
                  onNavigate('/checkout');
                }}
                className="w-full bg-ink text-ink-inverted py-3 px-4 rounded-sm text-xs uppercase tracking-widest font-medium flex items-center justify-center gap-2 hover:bg-primary transition-colors group"
              >
                Finalizar Compra
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>

              <button
                onClick={() => {
                  closeCart();
                  onNavigate('/carrinho');
                }}
                className="w-full text-center text-xs text-ink-muted hover:text-ink py-1 transition-colors underline underline-offset-4"
              >
                Ver Sacola Completa
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
